import Phaser from 'phaser';
import { itemManager } from '../../managers/ItemManager';

export default class WeaponSelectionUI extends Phaser.GameObjects.Container {
    constructor(scene, x = 0, y = 0) {
        super(scene, x, y);
        this.scene = scene;
        
        // Debug mode state
        this.debugMode = false;
        
        // UI state
        this.isVisible = false;
        this.hoveredSlot = null;
        
        // UI dimensions
        this.viewportWidth = 256;
        this.viewportHeight = 192;
        
        // Weapon selection slots - using actual weapon data from items.json
        this.weaponSlots = this.initializeWeaponSlots();
        
        this.slotSize = 27;
        
        this.createUI();
        this.setupInputHandlers();
        
        scene.add.existing(this);
        
        // Set high depth and fix to screen coordinates
        this.setDepth(550);
        this.setScrollFactor(0);
        
        // Start hidden
        this.setVisible(false);
    }
    
    initializeWeaponSlots() {
        // Get weapon data from items.json and create slots dynamically
        const weaponSlots = [];
        const positions = [
            { x: 98, y: 75 },   // Row 1, Col 1
            { x: 131, y: 75 },  // Row 1, Col 2
            { x: 164, y: 75 },  // Row 1, Col 3
            { x: 98, y: 105 },  // Row 2, Col 1
            { x: 131, y: 105 }, // Row 2, Col 2
            { x: 164, y: 105 }  // Row 2, Col 3
        ];
        
        for (let i = 0; i < 5; i++) {
            const weaponData = itemManager.getItemData(`Weapon_${i}`);
            if (weaponData) {
                weaponSlots.push({
                    x: positions[i].x,
                    y: positions[i].y,
                    weaponId: `Weapon_${i}`,
                    name: weaponData.name,
                    textureKey: weaponData.textureKey,
                    spritePath: weaponData.spritePath,
                    enabled: i === 0 || i === 4 // Only Basic Sword (0) and Basic Bow (4) enabled for now
                });
            }
        }
        
        return weaponSlots;
    }
    
    createUI() {
        // Create the main UI background (reuse tool selection UI)
        this.background = this.scene.add.image(this.viewportWidth / 2, this.viewportHeight / 2, 'weapon-selection-ui');
        this.background.setOrigin(0.5);
        this.background.setScrollFactor(0);
        this.background.setDepth(550);
        this.background.setVisible(false);
        
        // Create hover overlay
        this.hoverOverlay = this.scene.add.image(0, 0, 'tool-ui-icon-empty-hover');
        this.hoverOverlay.setVisible(false);
        this.hoverOverlay.setScrollFactor(0);
        this.hoverOverlay.setDepth(552);
        
        // Create weapon icons
        this.weaponIcons = [];
        this.weaponSlots.forEach((slot, index) => {
            const icon = this.createWeaponIcon(slot);
            if (icon) {
                this.weaponIcons.push(icon);
            }
        });
        
        // Create debug graphics
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.setScrollFactor(0);
        this.debugGraphics.setDepth(560);
        
        // Create interactive areas for weapon slots
        this.createSlotInteractiveAreas();
        
        // Create close button
        this.setupCloseButton();
        
        // Setup tooltip
        this.setupTooltip();
    }
    
    createWeaponIcon(slot) {
        //console.log("createWeaponIcon: ", slot);
        // Get weapon data first
        const weaponData = itemManager.getItemData(slot.weaponId);
        
        // Use textureKey from weapon data, with fallback
        let textureKey = slot.textureKey;
        
        // Check if texture exists, use fallback if not
        if (!this.scene.textures.exists(textureKey)) {
            console.warn(`Texture ${textureKey} not found, using fallback`);
            // Fallback to generic textures based on weapon type
            if (weaponData && weaponData.subtype === 'Ranged') {
                textureKey = 'weapon_bow_basic';
            } else {
                textureKey = 'weapon_sword_basic';
            }
        }

        let icon;
        // Create the icon sprite
        if (weaponData && weaponData.id === 'weapon_bow_basic') {
            //console.log("bow textureKey: ", textureKey);
            icon = this.scene.add.image(slot.x, slot.y, textureKey, 1);
        } else {
            icon = this.scene.add.image(slot.x, slot.y, textureKey);
        }
        icon.setOrigin(0.5, 0.5);
        icon.setScrollFactor(0);
        icon.setDepth(553); // Above hover overlay but below interaction areas
        icon.setVisible(false);
        
        // Make disabled weapons transparent
        if (!slot.enabled) {
            icon.setAlpha(0.3);
        }
        
        return icon;
    }
    
    createSlotInteractiveAreas() {
        this.slotAreas = [];
        
        this.weaponSlots.forEach((slot, index) => {
            const area = this.scene.add.rectangle(slot.x, slot.y, this.slotSize, this.slotSize, 0x000000, 0);
            area.setInteractive();
            area.setScrollFactor(0);
            area.setDepth(555);
            area.setVisible(false);
            
            area.slotIndex = index;
            area.weaponData = slot;
            
            // Setup hover events
            area.on('pointerover', () => {
                if (this.isVisible) {
                    this.hoveredSlot = index;
                    
                    if (slot.enabled) {
                        this.hoverOverlay.setPosition(slot.x, slot.y);
                        this.hoverOverlay.setVisible(true);
                    }
                    
                    this.updateTooltip(area);
                    this.tooltip.setVisible(true);
                }
            });

            area.on('pointerout', () => {
                this.hoveredSlot = null;
                this.hoverOverlay.setVisible(false);
                this.tooltip.setVisible(false);
            });
            
            // Click handlers - separate from weapon usage
            area.on('pointerdown', (pointer) => {
                if (!this.isVisible) return;
                
                if (!slot.enabled) {
                    //console.log(`${slot.name} is not available yet - Coming Soon!`);
                    return;
                }
                
                const weaponId = slot.weaponId;
                
                if (pointer.leftButtonDown()) {
                    // Set blocking flag to prevent weapon actions during equipping
                    this.scene.uiInteractionBlocked = true;
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    //console.log(`Equipping ${slot.name} to Weapon Slot 1`);
                    this.equipWeaponToSlot(weaponId, 1);
                } else if (pointer.rightButtonDown()) {
                    pointer.event.preventDefault();
                    // Set blocking flag to prevent weapon actions during equipping
                    this.scene.uiInteractionBlocked = true;
                    this.scene.time.delayedCall(100, () => {
                        this.scene.uiInteractionBlocked = false;
                    });
                    //console.log(`Equipping ${slot.name} to Weapon Slot 2`);
                    this.equipWeaponToSlot(weaponId, 2);
                }
            });
            
            this.slotAreas.push(area);
        });
    }
    
    setupInputHandlers() {
        // Set up keyboard input for opening/closing weapon selection UI
        // Changed from 'W' to 'T' key
        this.scene.input.keyboard.on('keydown-T', () => {
            if (!this.scene.uiInteractionBlocked) {
                this.toggle();
            }
        });
    }
    
    setupCloseButton() {
        // Create close button (X)
        this.closeButton = this.scene.add.image(this.viewportWidth / 2 + 100, this.viewportHeight / 2 - 75, 'Windows_Button_X');
        this.closeButton.setScrollFactor(0);
        this.closeButton.setDepth(556);
        this.closeButton.setVisible(false);
        this.closeButton.setInteractive();
        
        this.closeButton.on('pointerover', () => {
            if (this.isVisible) {
                this.closeButton.setTexture('Windows_Button_Pressed_X');
            }
        });
        
        this.closeButton.on('pointerout', () => {
            this.closeButton.setTexture('Windows_Button_X');
        });
        
        this.closeButton.on('pointerdown', () => {
            if (this.isVisible) {
                this.hide();
            }
        });
    }
    
    setupTooltip() {
        // Create tooltip container
        this.tooltip = this.scene.add.container(0, 0);
        this.tooltip.setScrollFactor(0);
        this.tooltip.setDepth(600);
        this.tooltip.setVisible(false);
        
        // Tooltip background
        this.tooltipBg = this.scene.add.rectangle(0, 0, 120, 50, 0x000000, 0.8);
        this.tooltip.add(this.tooltipBg);
        
        // Tooltip text elements
        this.titleText = this.scene.add.text(0, -15, '', {
            fontFamily: 'Arial',
            fontSize: '12px',
            color: '#ffffff',
            fontStyle: 'bold'
        });
        this.titleText.setOrigin(0.5);
        this.tooltip.add(this.titleText);
        
        this.typeText = this.scene.add.text(0, -3, '', {
            fontFamily: 'Arial',
            fontSize: '10px',
            color: '#888888'
        });
        this.typeText.setOrigin(0.5);
        this.tooltip.add(this.typeText);
        
        this.descriptionText = this.scene.add.text(0, 10, '', {
            fontFamily: 'Arial',
            fontSize: '9px',
            color: '#cccccc',
            wordWrap: { width: 120 }
        });
        this.descriptionText.setOrigin(0.5);
        this.tooltip.add(this.descriptionText);
    }
    
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }
    
    show() {
        this.isVisible = true;
        this.setVisible(true);
        
        // Show all UI elements
        this.background.setVisible(true);
        this.closeButton.setVisible(true);
        this.weaponIcons.forEach(icon => icon.setVisible(true));
        this.slotAreas.forEach(area => area.setVisible(true));
        
        //console.log('Weapon Selection UI opened');
    }
    
    hide() {
        this.isVisible = false;
        this.setVisible(false);
        
        // Hide all UI elements
        this.background.setVisible(false);
        this.closeButton.setVisible(false);
        this.hoverOverlay.setVisible(false);
        this.tooltip.setVisible(false);
        this.weaponIcons.forEach(icon => icon.setVisible(false));
        this.slotAreas.forEach(area => area.setVisible(false));
        
        //console.log('Weapon Selection UI closed');
    }
    
    // Equip weapon to specified slot
    async equipWeaponToSlot(weaponId, slotNumber) {
        try {
            // Update the WeaponUI display
            if (this.scene.weaponUI) {
                this.scene.weaponUI.updateEquippedWeapon(slotNumber, weaponId);
            }
            
            // TODO: Make API call to save equipment state when backend is ready
            // await this.saveWeaponEquipmentState(slotNumber, weaponId);
                        
            // Close weapon selection UI after equipping
            this.hide();
        } catch (error) {
            console.error('Failed to equip weapon:', error);
        }
    }
    
    // Save equipment state to backend (placeholder for future implementation)
    async saveWeaponEquipmentState(slotNumber, weaponId) {
        try {
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, cannot save weapon equipment state');
                return;
            }

            // Create update object with only the relevant slot
            const updateData = {};
            if (slotNumber === 1) {
                updateData.weaponslot_1_equipped = weaponId;
            } else if (slotNumber === 2) {
                updateData.weaponslot_2_equipped = weaponId;
            }

            // Update user options
            await gameService.updateUserOptions(updateData);            
        } catch (error) {
            console.error('Error saving weapon equipment state:', error);
        }
    }
    
    // Update tooltip content
    updateTooltip(area) {
        const slot = area.weaponData;
        const weaponData = itemManager.getItemData(slot.weaponId);
        
        this.titleText.setText(slot.name);
        this.titleText.setColor('#ffffff');
        this.typeText.setText(weaponData ? weaponData.subtype : 'Weapon');
        
        // Show different description for disabled weapons
        if (!slot.enabled) {
            this.descriptionText.setText('Coming Soon! This weapon is not available yet.');
            this.descriptionText.setFontStyle("italic");
            this.titleText.setColor('#888888'); // Gray out the title
        } else {
            // Get weapon description from items.json
            const description = weaponData ? weaponData.description : 'A weapon for combat.';
            this.descriptionText.setText(description);
            this.descriptionText.setFontStyle("italic");
        }
        
        // Calculate tooltip dimensions and position
        let totalHeight = 3 + 
            this.titleText.height + 
            this.typeText.height +
            this.descriptionText.height + 
            12;
        
        const largestWidth = Math.max(
            this.titleText.width, 
            this.typeText.width, 
            this.descriptionText.width
        );

        this.tooltipBg.setSize(largestWidth + 6, totalHeight);

        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        const TOOLTIP_MARGIN = 5;

        let tooltipX = slot.x + 16;
        let tooltipY = slot.y + 16;

        // Adjust horizontal position
        if (tooltipX + largestWidth > UI_WIDTH) {
            tooltipX = slot.x - largestWidth - TOOLTIP_MARGIN;
        }

        if (tooltipX < 0) {
            tooltipX = TOOLTIP_MARGIN;
        }

        // Adjust vertical position
        if (tooltipY + totalHeight > UI_HEIGHT) {
            tooltipY = slot.y - totalHeight - TOOLTIP_MARGIN;
        }

        if (tooltipY < 0) {
            tooltipY = TOOLTIP_MARGIN;
        }

        this.tooltip.setPosition(tooltipX, tooltipY);
    }
    
    updateDebugVisuals() {
        if (!this.debugGraphics) return;
        
        this.debugGraphics.clear();
        
        if (this.debugMode) {
            // Draw slot areas
            this.debugGraphics.lineStyle(2, 0x00ff00, 0.8);
            this.debugGraphics.fillStyle(0x00ff00, 0.1);
            
            this.weaponSlots.forEach(slot => {
                const rect = new Phaser.Geom.Rectangle(
                    slot.x - this.slotSize / 2,
                    slot.y - this.slotSize / 2,
                    this.slotSize,
                    this.slotSize
                );
                this.debugGraphics.strokeRectShape(rect);
                this.debugGraphics.fillRectShape(rect);
            });
            
            // Add debug text
            if (!this.debugText) {
                this.debugText = this.scene.add.text(10, 50, '', {
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    color: '#00ff00'
                });
                this.debugText.setScrollFactor(0);
                this.debugText.setDepth(600);
            }
            
            this.debugText.setText([
                'Weapon Selection UI Debug Mode',
                `Visible: ${this.isVisible}`,
                `Hovered: ${this.hoveredSlot !== null ? this.weaponSlots[this.hoveredSlot].name : 'none'}`,
                'Left Click: Assign to Slot 1',
                'Right Click: Assign to Slot 2',
                'Toggle Key: T (changed from W)',
                'Weapons: ' + this.weaponSlots.map(slot => slot.name).join(', ')
            ]);
            this.debugText.setVisible(true);
        } else {
            if (this.debugText) {
                this.debugText.setVisible(false);
            }
        }
    }
    
    destroy() {
        // Clean up all created objects
        if (this.background) this.background.destroy();
        if (this.hoverOverlay) this.hoverOverlay.destroy();
        if (this.debugText) this.debugText.destroy();
        if (this.debugGraphics) this.debugGraphics.destroy();
        if (this.tooltip) this.tooltip.destroy();
        
        this.weaponIcons.forEach(icon => icon.destroy());
        this.slotAreas.forEach(area => area.destroy());
        if (this.closeButton) this.closeButton.destroy();
        
        super.destroy();
    }
} 